import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { LearningModule, UserProgress, TaxonomyLevel } from '@/types/supabase';
import { Link } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { Badge } from '@/components/ui/badge';
import { BookOpen, Lock, Brain, Sparkles } from 'lucide-react';
import { useAuth } from '@/contexts/auth/AuthContext';
import TaxonomyBadge from '@/components/learning/TaxonomyBadge';

const ModulesList = () => {
  const { user } = useAuth();
  const [modules, setModules] = useState<LearningModule[]>([]);
  const [progress, setProgress] = useState<UserProgress[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const fetchModules = async () => {
      try {
        setIsLoading(true);
        
        const { data: modulesData, error: modulesError } = await supabase
          .from('learning_modules')
          .select('*')
          .order('order_position', { ascending: true });
        
        if (modulesError) throw modulesError;
        
        const typedModules = modulesData.map(module => ({
          ...module,
          phase: module.phase as LearningModule['phase'],
          track: module.track as LearningModule['track'],
          taxonomy_level: module.taxonomy_level as TaxonomyLevel[] || []
        }));
        
        setModules(typedModules);
        
        if (user) {
          const { data: progressData, error: progressError } = await supabase
            .from('user_progress')
            .select('*')
            .eq('user_id', user.id);
          
          if (progressError) throw progressError;
          setProgress(progressData);
        }
      } catch (error) {
        console.error('Error fetching modules:', error);
        toast({
          title: "Error loading modules",
          description: "There was a problem loading the learning modules.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchModules();
  }, [user, toast]);

  const getModuleProgress = (moduleId: string) => {
    const moduleProgress = progress.find(p => p.module_id === moduleId);
    return moduleProgress ? moduleProgress.completion_percentage : 0;
  };

  const isModuleLocked = (moduleIndex: number) => {
    if (moduleIndex === 0) return false;
    
    const prevModule = modules[moduleIndex - 1];
    if (!prevModule) return false;
    
    return getModuleProgress(prevModule.id) < 80;
  };

  const getDifficultyColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'beginner':
        return 'bg-green-200 text-green-900 dark:bg-green-800 dark:text-green-100';
      case 'intermediate':
        return 'bg-blue-200 text-blue-900 dark:bg-blue-800 dark:text-blue-100';
      case 'advanced':
        return 'bg-purple-200 text-purple-900 dark:bg-purple-800 dark:text-purple-100';
      default:
        return 'bg-gray-200 text-gray-900 dark:bg-gray-800 dark:text-gray-100';
    }
  };

  const getTaxonomyBadge = (taxonomyLevel: string[] | null) => {
    if (!taxonomyLevel || taxonomyLevel.length === 0) return null;
    
    const highestLevel = taxonomyLevel[taxonomyLevel.length - 1] as TaxonomyLevel;
    
    return <TaxonomyBadge taxonomyLevel={highestLevel} />;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
        <div>
          <h1 className="text-2xl font-bold">Learning Modules</h1>
          <p className="text-muted-foreground">Progress through the bootcamp curriculum</p>
        </div>
      </div>

      <div className="grid gap-6">
        {modules.map((module, index) => {
          const locked = isModuleLocked(index);
          const progress = getModuleProgress(module.id);
          const inProgress = progress > 0 && progress < 100;
          const completed = progress === 100;
          
          return (
            <Card key={module.id} className={locked ? 'opacity-75' : ''}>
              <CardHeader>
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                  <div className="space-y-1">
                    <CardTitle className="flex items-center">
                      {module.title}
                      {locked && <Lock className="h-4 w-4 ml-2 text-muted-foreground" />}
                    </CardTitle>
                    <CardDescription>{module.description}</CardDescription>
                  </div>
                  <div className="flex items-center space-x-2 mt-2 md:mt-0">
                    <Badge className={getDifficultyColor(module.difficulty_level)}>
                      {module.difficulty_level}
                    </Badge>
                    {getTaxonomyBadge(module.taxonomy_level)}
                    {module.taxonomy_level?.includes('create') && (
                      <Badge variant="outline" className="border-primary text-primary bg-primary/10">
                        <Sparkles className="mr-1 h-3 w-3" /> Second Me
                      </Badge>
                    )}
                    {completed && (
                      <Badge variant="outline" className="border-green-500 text-green-700 bg-green-100">
                        Completed
                      </Badge>
                    )}
                    {inProgress && (
                      <Badge variant="outline" className="border-blue-500 text-blue-700 bg-blue-100">
                        In Progress
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <BookOpen className="h-5 w-5 mr-2 text-muted-foreground" />
                      <span className="text-sm">Module {index + 1}</span>
                    </div>
                    <span className="text-sm font-medium">{progress}% complete</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </div>
              </CardContent>
              <CardFooter>
                <Button 
                  className="w-full" 
                  variant={locked ? "outline" : "default"}
                  disabled={locked}
                  asChild={!locked}
                >
                  {!locked ? (
                    <Link to={`/modules/${module.id}`}>
                      {progress > 0 ? 'Continue Module' : 'Start Module'}
                    </Link>
                  ) : (
                    "Complete previous module to unlock"
                  )}
                </Button>
              </CardFooter>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default ModulesList;
